import { AppPermission } from "@closerplatform/spinner-openapi";
import { observer } from "mobx-react";
import React, { useState, useEffect } from "react";
import { withPermissionFallback } from "../../HOC/withPermissionFallback/withPermissionFallback";
import settingsStyles from "../../../containers/settings/settingsEditCommon.css";
import { intl, L10n } from "@fidget/common/i18n";
import { SubtitleDescription } from "../subtitleDescription";
import { ActivateTransferCheckbox } from "./components/ActivateTransferCheckbox/ActivateTransferCheckbox";
import { createEmptyRule, mapRuleToForm, mapFormToRule, copyTagRuleForm } from "./utils";
import { usePreloader, withPreloading } from "../../HOC/withPreloading/withPreloading";
import { LoadingState } from "../../HOC/withPreloading/types";
import { useStore } from "../../../models";
import { ITagGroup } from "../../../models/tagGroups/TagGroupStore";
import { FormPlaceholder } from "../settingsForms/fullWidthPlaceholder/formPlaceholder";
import { ElementForm } from "../settingsForms/elementForm/elementForm/elementForm";
import { TagRuleBox } from "./components/TagRuleBox/TagRuleBox";
import { VerticalSeparator } from "../settingsForms/verticalSeparator";
import { ElementFormSaveDiscardIcon } from "../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { ElementFormActions } from "../settingsForms/elementForm/actions/elementFormActions";
import { TagGroupTransferRuleForm } from "./types";
import { SearchingInput } from "../../dashboard/searchingInput/searchingInput";
import styles from "./ManualAssignmentSettings.css";
import { ElementFormContent } from "../settingsForms/elementForm/content/elementFormContent";
import { ElementFormEditIcon } from "../settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormDeleteIcon } from "../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { DeleteConfirmationToastOnError } from "../settingsForms/deleteConfirmation/deleteConfirmation";
import { isEmpty } from "lodash";
import { useAgentServices } from "../../../models/agentServices";
import { SpinnerService } from "../../../services/spinner/spinnerService";
import { logger } from "@fidget/common/utils";

export const ManualAssignmentSettingsComponent = observer(() => {
  const {
    allItems,
    enabled,
    newItem,
    editedItems,
    setEditedItems,
    apiPatchItem,
    handleCreateNewClick,
    discardNewItem,
    apiPatchEnabled,
    apiSaveNewItem,
    searchFilter,
    setSearchFilter,
    allTagGroups,
    unusedTagGroups,
    discardItem,
    uiRemoveExistingItem,
    spinnerService
  } = useManualAssignmentSettings();

  const renderHeaderSection = () => {
    return (
      <div>
        <div className={settingsStyles.title}>
          <L10n id={"settings.assignmentRules.manualAssignment.title"} />
        </div>
        <div className={settingsStyles.section}>
          <SubtitleDescription
            subtitle={intl.formatMessage("settings.assignmentRules.manualAssignment.activation")}
            description={intl.formatMessage(
              "settings.assignmentRules.manualAssignment.activationDescription"
            )}
          />
          <div className={settingsStyles.content}>
            <ActivateTransferCheckbox value={enabled} onSave={apiPatchEnabled} />
          </div>
        </div>
      </div>
    );
  };

  const renderNewTagSection = () => {
    return newItem ? (
      <ElementForm isEdited>
        <ElementFormContent>
          <TagRuleBox
            hasError={newItem.hasError}
            form={newItem}
            allTagGroups={allTagGroups}
            unusedTagGroups={unusedTagGroups}
            isEditing
            isNewItem
          />
        </ElementFormContent>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormSaveDiscardIcon onSave={apiSaveNewItem} onDiscard={discardNewItem} />
        </ElementFormActions>
      </ElementForm>
    ) : (
      <FormPlaceholder
        onClick={handleCreateNewClick}
        text={`+ ${intl.formatMessage("settings.assignmentRules.manualAssignment.createNew")}`}
      />
    );
  };

  const renderDescriptionSection = () => {
    return (
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.assignmentRules.manualAssignment.ruleList")}
        description={intl.formatMessage(
          "settings.assignmentRules.manualAssignment.ruleListDescription"
        )}
      />
    );
  };

  const renderSearch = () => {
    return (
      <div className={styles["search-filter"]}>
        <SearchingInput
          value={searchFilter}
          onChange={setSearchFilter}
          placeholder={intl.formatMessage("settings.assignmentRules.manualAssignment.search")}
        />
      </div>
    );
  };

  const renderRuleList = () => {
    const filteredItems = searchFilter
      ? allItems.filter((item) =>
          allTagGroups
            .find((allTagGroup) => allTagGroup.id === item.$.tagGroupId.$)
            ?.name.startsWith(searchFilter)
        )
      : allItems;

    if (isEmpty(filteredItems)) {
      return (
        <SubtitleDescription
          description={intl.formatMessage("settings.assignmentRules.manualAssignment.noRules")}
        />
      );
    }

    return (
      <div>
        {filteredItems.map((tagGroup) => {
          const tagGroupId = tagGroup.$.tagGroupId.$;
          const editedItem = editedItems.find(
            (editItem) => tagGroup.$.tagGroupId.$ === editItem.$.tagGroupId.$
          );

          return editedItem ? (
            renderEditedForm(editedItem)
          ) : (
            <DeleteConfirmationToastOnError
              key={tagGroupId}
              areYouSureText={intl.formatMessage(
                "settings.assignmentRules.manualAssignment.areYouSureToRemove"
              )}
              succesfullyDeletedText={intl.formatMessage(
                "settings.assignmentRules.manualAssignment.removedSuccessfully"
              )}
              onTimeoutPassed={() => uiRemoveExistingItem(tagGroupId)}
              onDelete={() => spinnerService.deleteTagGroupRule(tagGroupId)}
              renderComponent={(onDeleteClick) => renderStaticForm(tagGroup, onDeleteClick)}
            />
          );
        })}
      </div>
    );
  };

  const renderEditedForm = (form: TagGroupTransferRuleForm) => {
    return (
      <ElementForm key={form.$.tagGroupId.$}>
        <ElementFormContent>
          <TagRuleBox
            hasError={form.hasError}
            form={form}
            allTagGroups={allTagGroups}
            unusedTagGroups={unusedTagGroups}
            isEditing
          />
        </ElementFormContent>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormSaveDiscardIcon
            onSave={() => apiPatchItem(form)}
            onDiscard={() => discardItem(form)}
          />
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderStaticForm = (form: TagGroupTransferRuleForm, onDeleteClick: () => void) => {
    return (
      <ElementForm>
        <ElementFormContent>
          <TagRuleBox
            hasError={form.hasError}
            form={form}
            allTagGroups={allTagGroups}
            unusedTagGroups={unusedTagGroups}
            isEditing={false}
          />
        </ElementFormContent>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormEditIcon
            onClick={() => setEditedItems([...editedItems, copyTagRuleForm(form)])}
          />
          <ElementFormDeleteIcon onClick={onDeleteClick!} />
        </ElementFormActions>
      </ElementForm>
    );
  };

  return (
    <div>
      {renderHeaderSection()}
      <div className={settingsStyles.section}>
        {renderDescriptionSection()}
        {renderNewTagSection()}
        {renderSearch()}
        {renderRuleList()}
      </div>
    </div>
  );
});

interface HookProps {
  enabled: boolean;
  allItems: TagGroupTransferRuleForm[];

  editedItems: TagGroupTransferRuleForm[];
  setEditedItems: (newItems: TagGroupTransferRuleForm[]) => void;

  handleCreateNewClick: () => void;
  newItem: TagGroupTransferRuleForm | undefined;
  setNewItem: (rule: TagGroupTransferRuleForm) => void;
  discardNewItem: () => void;

  allTagGroups: ITagGroup[];
  unusedTagGroups: ITagGroup[];

  searchFilter: string;
  setSearchFilter: (filter: string) => void;

  apiPatchEnabled: (newState: boolean) => void;
  apiPatchItem: (patchedRule: TagGroupTransferRuleForm) => void;
  apiSaveNewItem: () => void;
  discardItem: (discardItem: TagGroupTransferRuleForm) => void;
  uiRemoveExistingItem: (tagGroupId: string) => void;

  spinnerService: SpinnerService;
}

const useManualAssignmentSettings = (): HookProps => {
  const { tagGroupsStore, tagGroupRulesStore, uiStore } = useStore();
  const { spinnerService } = useAgentServices();
  const [allItems, setAllItems] = useState<TagGroupTransferRuleForm[]>([]);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [newItem, setNewItem] = useState<TagGroupTransferRuleForm>();
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [editedItems, setEditedItems] = useState<TagGroupTransferRuleForm[]>([]);
  const { setLoadingState } = usePreloader();

  const handleCreateNewClick = () => {
    setNewItem(createEmptyRule());
  };

  const discardItem = (discardedItem: TagGroupTransferRuleForm) => {
    setEditedItems((prevState) =>
      prevState.filter((item) => item.$.tagGroupId.$ !== discardedItem.$.tagGroupId.$)
    );
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        tagGroupRulesStore.loadRules(),
        tagGroupRulesStore.loadParameters(),
        tagGroupsStore.loadTagGroups()
      ]);

      const rules = tagGroupRulesStore.allRules;
      const storeEnabled = tagGroupRulesStore.isEnabled;
      setAllItems(rules.map(mapRuleToForm));
      setEnabled(storeEnabled);
      setLoadingState(LoadingState.Loaded);
    };
    loadData();
  }, []);

  const apiPatchItem = async (patchedRuleForm: TagGroupTransferRuleForm) => {
    const validation = await patchedRuleForm.validate();
    if (validation.hasError) {
      uiStore.addErrorToast(
        intl.formatMessage("settings.assignmentRules.manualAssignment.form.validationError")
      );
      return;
    }

    const editedRule = mapFormToRule(patchedRuleForm);
    try {
      await spinnerService.patchTagGroupRule(editedRule.tagGroupId, {
        targetTagGroupIds: editedRule.targetTagGroupIds
      });
    } catch (e) {
      logger.error({ message: "Couldn't patch tag group transfer rule", error: e });
      uiStore.addGenericErrorToast();
    }

    const updatedAllItems = [
      ...allItems.map((rule) =>
        rule.$.tagGroupId.$ === patchedRuleForm.$.tagGroupId.$ ? patchedRuleForm : rule
      )
    ];

    setEditedItems((prevState) =>
      prevState.filter((item) => item.$.tagGroupId.$ !== patchedRuleForm.$.tagGroupId.$)
    );
    setAllItems(updatedAllItems);
  };

  const apiPatchEnabled = async (newState: boolean) => {
    try {
      await spinnerService.patchTagGroupTransfersParameters({ enabled: newState });
    } catch (e) {
      logger.error({ message: "Couldn't patch transfer parameters topics", error: e });
      uiStore.addGenericErrorToast();
    }
    setEnabled(newState);
  };

  const discardNewItem = () => {
    setNewItem(undefined);
  };

  const apiSaveNewItem = async () => {
    if (!newItem) {
      return;
    }

    const validation = await newItem.validate();
    if (validation.hasError) {
      uiStore.addErrorToast(
        intl.formatMessage("settings.assignmentRules.manualAssignment.form.validationError")
      );
      return;
    }
    try {
      await spinnerService.createTagGroupRules(mapFormToRule(newItem));
    } catch (e) {
      logger.error({ message: "Couldn't create tag group transfer rule", error: e });
      uiStore.addGenericErrorToast();
    }

    const updatedAllItems = [newItem, ...allItems];
    setAllItems(updatedAllItems);
    setNewItem(undefined);
  };

  const uiRemoveExistingItem = (tagGroupId: string) => {
    const updatedAllItems = allItems.filter((rule) => rule.$.tagGroupId.$ !== tagGroupId);
    setAllItems(updatedAllItems);
  };

  const allTagGroups = tagGroupsStore.groups;
  const unusedTagGroups = allTagGroups.filter(
    (tagGroup) => !allItems.some((rule) => rule.$.tagGroupId.$ === tagGroup.id)
  );

  return {
    uiRemoveExistingItem,
    discardItem,
    editedItems,
    setEditedItems,
    allItems,
    allTagGroups,
    unusedTagGroups,
    newItem,
    handleCreateNewClick,
    discardNewItem,
    setNewItem,
    enabled,
    apiPatchItem,
    apiPatchEnabled,
    apiSaveNewItem,
    searchFilter,
    setSearchFilter,
    spinnerService
  };
};

export const ManualAssignmentSettings = withPermissionFallback<{}>(
  AppPermission.SettingsGeneral,
  withPreloading(ManualAssignmentSettingsComponent)
);
